import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix for default markers in Leaflet with Vite
// Fix for Leaflet marker icons in Vite
import markerIcon from 'leaflet/dist/images/marker-icon.png?url'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png?url'
import markerShadow from 'leaflet/dist/images/marker-shadow.png?url'

// Fix default icon paths
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
})

// Helper function to calculate time ago
const getTimeAgo = (timestamp: string): string => {
  const now = new Date()
  const time = new Date(timestamp)
  const diffInMinutes = Math.floor((now.getTime() - time.getTime()) / (1000 * 60))

  if (diffInMinutes < 1) return 'Just now'
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`

  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) return `${diffInHours}h ago`

  const diffInDays = Math.floor(diffInHours / 24)
  return `${diffInDays}d ago`
}

export interface MapMarker {
  id: string
  position: [number, number]
  title: string
  description?: string
  type: 'ping' | 'listing' | 'user'
  status?: 'active' | 'claimed' | 'expired' | 'available'
  price?: number
  timestamp?: string
  photo?: string
  userId?: string
}

interface MapProps {
  center?: [number, number]
  zoom?: number
  markers?: MapMarker[]
  onMarkerClick?: (marker: MapMarker) => void
  onMapClick?: (lat: number, lng: number) => void
  className?: string
  height?: string
  userLocation?: [number, number]
  isPingModeActive?: boolean
  pingRadius?: number
}

export interface MapRef {
  centerMap: (lat: number, lng: number, zoomLevel?: number) => void
}

const Map = forwardRef<MapRef, MapProps>(({
  center = [40.7128, -74.0060], // Default to NYC
  zoom = 13,
  markers = [],
  onMarkerClick,
  onMapClick,
  className = '',
  height = '100%',
  userLocation,
  isPingModeActive = false,
  pingRadius = 500
}, ref) => {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<L.Map | null>(null)
  const markersRef = useRef<L.Marker[]>([])
  const pingCircleRef = useRef<L.Circle | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return

    const map = L.map(mapRef.current).setView(center, zoom)

    // Add OpenStreetMap tiles (free)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map)

    // Handle map clicks
    if (onMapClick) {
      map.on('click', (e) => {
        onMapClick(e.latlng.lat, e.latlng.lng)
      })
    }

    mapInstanceRef.current = map
    setIsLoaded(true)

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
      if (pingCircleRef.current) {
        pingCircleRef.current = null
      }
    }
  }, [center, zoom, onMapClick])

  // Update markers when they change
  useEffect(() => {
    if (!mapInstanceRef.current || !isLoaded) return

    // Clear existing markers
    markersRef.current.forEach(marker => {
      mapInstanceRef.current?.removeLayer(marker)
    })
    markersRef.current = []

    // Add new markers
    markers.forEach(markerData => {
      const marker = L.marker(markerData.position)

      // Create popup content
      let popupContent = `<div class="p-2 max-w-xs">
        <h3 class="font-semibold text-sm">${markerData.title}</h3>`

      if (markerData.description) {
        popupContent += `<p class="text-xs text-gray-600 mt-1">${markerData.description}</p>`
      }

      // Add photo if available
      if (markerData.photo) {
        popupContent += `<img src="${markerData.photo}" alt="Parking spot" class="w-full h-20 object-cover rounded mt-2" />`
      }

      // Add timestamp if available
      if (markerData.timestamp) {
        const timeAgo = getTimeAgo(markerData.timestamp)
        popupContent += `<p class="text-xs text-gray-500 mt-1">📅 ${timeAgo}</p>`
      }

      if (markerData.price) {
        popupContent += `<p class="text-xs font-medium text-green-600 mt-1">$${markerData.price}/hr</p>`
      }

      if (markerData.status) {
        const statusColor = markerData.status === 'active' || markerData.status === 'available' ? 'green' :
                           markerData.status === 'claimed' ? 'orange' : 'red'
        popupContent += `<span class="inline-block px-2 py-1 text-xs rounded-full bg-${statusColor}-100 text-${statusColor}-800 mt-1">${markerData.status}</span>`
      }
      
      popupContent += '</div>'
      
      marker.bindPopup(popupContent)

      // Handle marker clicks
      if (onMarkerClick) {
        marker.on('click', () => {
          onMarkerClick(markerData)
        })
      }

      marker.addTo(mapInstanceRef.current!)
      markersRef.current.push(marker)
    })
  }, [markers, isLoaded, onMarkerClick])

  // Handle ping radius circle
  useEffect(() => {
    if (!mapInstanceRef.current || !isLoaded) return

    // Remove existing ping circle
    if (pingCircleRef.current) {
      mapInstanceRef.current.removeLayer(pingCircleRef.current)
      pingCircleRef.current = null
    }

    // Add ping circle if ping mode is active and user location is available
    if (isPingModeActive && userLocation) {
      const circle = L.circle(userLocation, {
        color: '#3b82f6',
        fillColor: '#3b82f6',
        fillOpacity: 0.1,
        radius: pingRadius,
        weight: 2,
        dashArray: '5, 5'
      }).addTo(mapInstanceRef.current)

      pingCircleRef.current = circle
    }
  }, [isPingModeActive, userLocation, pingRadius, isLoaded])

  // Method to center map on coordinates
  const centerMap = (lat: number, lng: number, zoomLevel?: number) => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([lat, lng], zoomLevel || zoom)
    }
  }

  // Expose centerMap method to parent components via ref
  useImperativeHandle(ref, () => ({
    centerMap
  }), [centerMap])

  return (
    <div
      ref={mapRef}
      className={`map-container ${className}`}
      style={{
        height,
        width: '100%',
        position: 'relative',
        zIndex: 1
      }}
    />
  )
})

Map.displayName = 'Map'

export default Map
